import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSiteName,
  setCcPhone,
  resetForm,
  setSiteStreet,
  setSiteZip,
  setSiteCity,
  setSiteState,
  setTimezone,
  setShiftStartHours,
  setShiftStartMinutes,
} from '../../../Redux/slices/siteSlice';
import { PulseLoader } from 'react-spinners';
import { LuTrash2 } from 'react-icons/lu';
import { IoCloudUpload } from 'react-icons/io5';
import styles from '../styles/styles.module.css';
import { states } from '../data/states';
// IMPORT UPDATE FUNCTIONS
import {
  createSite,
  createSiteFormData,
  updateSite,
  updateSiteFormData,
} from '../../../api/site/post';
import { addSite, updateSiteInList } from '../../../Redux/slices/allSites';
import ModalSmall from '../../../components/ModalSmall/ModalSmall';
import SignatureModal from '../../../components/SignatureModal/SignatureModal';
import { allTimezones } from '../data/timezones';
import { hours } from '../data/hours';
import { minutes } from '../data/minutes';

interface AddSiteInterface {
  setAddSiteActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddSite: React.FC<AddSiteInterface> = ({ setAddSiteActive }) => {
  const [formData, setFormData] = useState<FormData | null>(null);
  const [ndaFile, setNDAFile] = useState<any>(null);
  const [ndaFileError, setNDAFileError] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [singleLoading, setSingleLoading] = useState(false);

  const dispatch = useDispatch();
  const siteState = useSelector((state: any) => state.site);
  console.log(ndaFileError);
  // 🔹 DETERMINE EDIT MODE
  const isEditMode = !!siteState.siteId;

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleNDAFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.pdf')) {
      setNDAFile(file);
      setNDAFileError('');
    } else {
      setNDAFileError('Please upload a valid PDF file.');
    }
    event.target.value = '';
  };

  const handleNDAFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.pdf')) {
      setNDAFile(file);
      setNDAFileError('');
    } else {
      setNDAFileError('Please upload a valid PDF file.');
    }
  };

  /** -----------------------------
   * Required-field helpers
   * ----------------------------*/
  const requiredMissing = () => {
    const missing: string[] = [];
    if (!siteState.siteName) missing.push('Site Name');
    if (!siteState.timezone) missing.push('Timezone');
    if (!siteState.shiftStartHours) missing.push('Hour');
    if (!siteState.shiftStartMinutes) missing.push('Minute');
    return missing;
  };

  // Called when user confirms modal (i.e., optional fields missing)
  function modalProcessor() {
    const missing = requiredMissing();
    if (missing.length > 0) {
      alert(
        `Missing required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`
      );
      return;
    }

    // Proceed (formData decides multipart vs json)
    if (formData) {
      submitIndividualSiteFormData(false);
    } else {
      submitIndividualSite(false);
    }
  }

  // Click on "Create/Update Site"
  function individualSubmissionProcessor() {
    const missing = requiredMissing();
    if (missing.length > 0) {
      alert(
        `Missing required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`
      );
      return;
    }

    // If optional fields are missing, show modal
    if (
      !siteState.ccPhone ||
      !siteState.siteStreet ||
      !siteState.siteCity ||
      !siteState.siteState ||
      !siteState.siteZip
    ) {
      setModalOpen(true);
      return;
    }

    // All good → submit as complete
    if (formData) {
      submitIndividualSiteFormData(true);
    } else {
      submitIndividualSite(true);
    }
  }

  async function submitIndividualSiteFormData(dataComplete: boolean) {
    setSingleLoading(true);

    try {
      (formData as any).set(
        'siteInfo',
        JSON.stringify({ ...siteState, dataComplete })
      );

      // 🔹 Branch Logic: Create vs Update
      let resp;
      if (isEditMode) {
        resp = await updateSiteFormData(formData);
      } else {
        resp = await createSiteFormData(formData);
      }

      if (resp?.success) {
        dispatch(resetForm());

        if (isEditMode) {
          dispatch(updateSiteInList(resp.data));
        } else {
          dispatch(addSite(resp.data));
        }

        setFormData(null);
        setAddSiteActive(false);
      } else {
        alert('Failed to upload site data!');
      }
    } catch (err) {
      console.error('Submission Error:', err);
      alert('Error submitting site data.');
    } finally {
      setSingleLoading(false);
    }
  }

  async function submitIndividualSite(dataComplete: Boolean) {
    setSingleLoading(true);
    try {
      const payload = {
        ...siteState,
        dataComplete,
      };

      // 🔹 Branch Logic: Create vs Update
      let resp;
      if (isEditMode) {
        resp = await updateSite(siteState.siteId, payload);
      } else {
        resp = await createSite(payload);
      }

      if (resp?.success) {
        dispatch(resetForm());

        if (isEditMode) {
          dispatch(updateSiteInList(resp.data));
        } else {
          dispatch(addSite(resp.data));
        }

        setAddSiteActive(false);
      }
    } catch (err) {
      console.log(err);
      alert('An error occurred');
    } finally {
      setSingleLoading(false);
    }
  }

  function hideForm() {
    setFormData(null);
    setNDAFile(null);
  }

  function formatUSPhoneNumber(value: string): string {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.substring(0, 10);

    if (limited.length < 4) {
      return limited;
    } else if (limited.length < 7) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  }

  return (
    <div className={styles.addSiteContainer}>
      {modalOpen && (
        <ModalSmall
          cancelHandler={() => setModalOpen(false)}
          submitHandler={modalProcessor}
          submitButtonText="Continue Anyway"
          loading={singleLoading}
          title="Attention"
          text="We value your privacy. If you choose not to fill out all the information, you will not be able to use our services to the fullest extent."
        />
      )}
      {ndaFile && !formData && (
        <SignatureModal
          setFormData={setFormData}
          onHide={hideForm}
          file={ndaFile}
        />
      )}
      <div className={styles.addSiteWrapper}>
        <div className={styles.addSiteLeft}>
          <div className={styles.addSiteLeft_left}>
            <div className={styles.inputContainer}>
              <div className={styles.required}>
                <p className={styles.label}>Site Name</p>
                <p className={styles.labelAsterix}>*</p>
              </div>

              <input
                required
                className={styles.inputElement}
                value={siteState.siteName}
                onChange={(e) => dispatch(setSiteName(e.target.value))}
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>{`CC Phone`}</p>
              <input
                className={styles.inputElement}
                value={siteState.ccPhone}
                onChange={(e) =>
                  dispatch(setCcPhone(formatUSPhoneNumber(e.target.value)))
                }
              />
            </div>

            <div className={styles.inputContainer}>
              <div className={styles.required}>
                <p className={styles.label}>Timezone</p>
                <p className={styles.labelAsterix}>*</p>
              </div>
              <select
                onChange={(e: any) => dispatch(setTimezone(e.target.value))}
                value={siteState.timezone}
                className={styles.timezoneDropdown}
                required
              >
                <option value="">Select Timezone...</option>
                {allTimezones.map((zone: string) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </div>

            <p className={styles.morningHeadline}>Morning Shift Start</p>
            <div className={styles.shiftTimeSelectorContainer}>
              <div className={styles.inputContainerSuperSmall}>
                <div className={styles.required}>
                  <p className={styles.timeLabel}>Hour</p>
                  <p className={styles.labelAsterix}>*</p>
                </div>
                <select
                  onChange={(e: any) =>
                    dispatch(setShiftStartHours(e.target.value))
                  }
                  value={siteState.shiftStartHours}
                  className={styles.inputHourContainer}
                  required
                >
                  <option value="">--</option>
                  {hours.map((hr: string) => (
                    <option key={hr} value={hr}>
                      {hr}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.inputContainerSuperSmall}>
                <div className={styles.required}>
                  <p className={styles.timeLabel}>Minute</p>
                  <p className={styles.labelAsterix}>*</p>
                </div>
                <select
                  onChange={(e: any) =>
                    dispatch(setShiftStartMinutes(e.target.value))
                  }
                  value={siteState.shiftStartMinutes}
                  className={styles.inputHourContainer}
                  required
                >
                  <option value="">--</option>
                  {minutes.map((min: string) => (
                    <option key={min} value={min}>
                      {min}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={styles.addSiteLeft_right}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Site Street</p>
              <input
                className={styles.inputElement}
                value={siteState.siteStreet}
                onChange={(e) => dispatch(setSiteStreet(e.target.value))}
              />
            </div>
            <div className={styles.inputContainer}>
              <p className={styles.label}>{`Site City`}</p>
              <input
                className={styles.inputElement}
                value={siteState.siteCity}
                onChange={(e) => dispatch(setSiteCity(e.target.value))}
              />
            </div>

            <div className={styles.stateAndZip}>
              <div className={styles.inputContainerSuperSmall}>
                <p className={styles.label}>Site State</p>
                <select
                  onChange={(e: any) => dispatch(setSiteState(e.target.value))}
                  value={siteState.siteState}
                  className={styles.stateDropdown}
                >
                  {states.map((state: string) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.inputContainerSmall}>
                <p className={styles.label}>Site Zip</p>
                <input
                  value={siteState.siteZip}
                  onChange={(e: any) => dispatch(setSiteZip(e.target.value))}
                  className={styles.inputElement}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.addSiteRight}>
          <div className={styles.profileCard}>
            <p className={styles.importUsersHeadline}>NDA</p>

            <div className={styles.smallBoxHr1}></div>

            <div
              className={styles.bigCloudSymbol}
              onDrop={handleNDAFileDrop}
              onDragOver={handleDragOver}
              onDragEnter={(e) => e.preventDefault()}
              onDragLeave={(e) => e.preventDefault()}
            >
              <IoCloudUpload />
              <p className={styles.dropFilesHere}>
                {ndaFile ? 'NDA added' : 'Drop NDA here...'}
              </p>
            </div>

            <div
              className={styles.cloudUploadBigButton}
              onClick={() => document.getElementById('NDAFileInput')!.click()}
            >
              Select files
            </div>

            <input
              type="file"
              id="NDAFileInput"
              style={{ display: 'none' }}
              onChange={handleNDAFileChange}
              accept=".pdf"
              multiple
            />

            <div className={styles.smallBoxHr2}></div>

            <div className={styles.checkBoxInSmallContainer}>
              {ndaFile ? (
                <>
                  <p className={styles.xlsFileName}>
                    {ndaFile.name.length > 15
                      ? ndaFile.name.substring(0, 15) + '…'
                      : ndaFile.name}
                  </p>
                  <LuTrash2 className={styles.trashSymbol} onClick={hideForm} />
                </>
              ) : (
                'No NDA File'
              )}
            </div>

            <div className={styles.smallBoxHr3}></div>
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      <button
        onClick={individualSubmissionProcessor}
        className={styles.createSiteBtn}
        disabled={singleLoading}
      >
        {singleLoading ? (
          <PulseLoader style={{ marginTop: '10px' }} color="white" size={5} />
        ) : isEditMode ? (
          'Update Site'
        ) : (
          'Create Site'
        )}
      </button>
    </div>
  );
};

export default AddSite;
